// The account pieces (header controls, account page, bug report page, settings
// dialog) bound to kindly's session by @jkast90/kast-common/account, once.
import { useLocation } from "react-router-dom";
import { createAccountKit } from "@jkast90/kast-common/account";
import { LegalLinks } from "@jkast90/kast-common/legal/react";
import { useAuth } from "@auth/hooks/useAuth";
import { session } from "../auth/session";
import { signOut, startSignIn } from "../auth/signIn";
import { themeStore } from "../theme";
import { useAdmin } from "./hooks/useAdmin";
import { useOwnProfile } from "./queries";
import { pictureSrc } from "./services/kindlyApi";
import { getAuthEnabled, getWorkspaceApiBaseUrl } from "./settings";

/**
 * The signed-in user's picture for the header's profile button: the same own-profile
 * query the Profile page reads (and "Change picture" updates), so a new pick shows at
 * once, and nothing is asked while signed out.
 */
const useOwnPicture = (): string | null => {
  const { isAuthenticated, user } = useAuth();
  const email = localStorage.getItem("userEmail") || user?.email || null;
  const own = useOwnProfile(email, isAuthenticated || !getAuthEnabled());
  return pictureSrc(own.data);
};

export const {
  useIdentity,
  AccountControls,
  AccountPage,
  BugReportPage,
  AppSettings: KitAppSettings,
} = createAccountKit({
  session,
  signIn: startSignIn,
  signOut,
  themeStore,
  admin: useAdmin,
  paths: { profile: "/profile" },
  apiUrl: getWorkspaceApiBaseUrl,
  // Still read from useAuth, which also answers for auth off (the bypass email).
  useIdentity: () => {
    const { isAuthenticated, isLoading, userId, userEmail } = useAuth();
    return {
      signedIn: isAuthenticated,
      loading: isLoading,
      id: userId,
      email: userEmail,
      name: null,
      displayName: userEmail,
    };
  },
  usePicture: useOwnPicture,
  useLocationState: () => useLocation().state,
  bugReport: { app: "kindly2" },
  settings: {
    adminLabel: "Admin override",
    adminDescription:
      "Shows edit and delete controls on everyone's events, wishes, memories and imprints, and the Admin link",
    legal: <LegalLinks newTab={false} />,
  },
});
